import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { SectionKeyType } from '../data-qa-constants';
import { ConfigSummary } from '../data-qa-types';
import { groupConfigsByModel, ModelGroup } from '../data-qa-utils';
import { loadConfig } from '../api/data-qa-api';

// A multi-config group (Brand Equity's two modeling stages) can genuinely disagree
// with each other on a given section -- confirmed 2026-08-25 on real `away` data that
// GQV's declared context_vars have drifted from Total Business's (three outlier flags
// present on one stage, absent on the other) -- Sean confirmed this is expected
// (the two stages are genuinely different models with good reason to encode different
// outliers), not a data-hygiene bug. An unqualified "Brand Equity" label would wrongly
// imply BOTH stages share this data when only the matching one(s) actually do, so name
// the specific stage(s) by their own dependentVarLabel ("GQV"/"Total Business")
// whenever the match isn't unanimous across the whole group.
const matchLabel = (group: ModelGroup, matchedConfigs: ConfigSummary[]): string => {
  const modelLabel = group.label;
  if (matchedConfigs.length === group.configs.length) return modelLabel;
  const stageLabels = matchedConfigs.map(c => c.dependentVarLabel).filter(Boolean);
  return stageLabels.length > 0 ? `${modelLabel} (${stageLabels.join('/')})` : modelLabel;
};

export interface SectionDataInvariance {
  // True while at least one other model's load is still in flight — we don't yet
  // know whether this section is shared, so the UI should show a transient "checking"
  // state rather than silently looking identical to a section with no matches at all.
  checking: boolean;
  // Every other MODEL (not config — Brand Equity's two stages count as one model)
  // whose section content hash matches the active model's, by display label. Named
  // explicitly (never a blanket "identical across models" claim) so a PARTIAL match —
  // some siblings share the data, one doesn't — can't be misread as "every model
  // matches." The same care applies WITHIN a multi-config model: if only one of Brand
  // Equity's two stages matches, the label names that stage (e.g. "Brand Equity (Total
  // Business)") rather than claiming the whole model matches — see matchLabel.
  matchingModelLabels: string[];
}

// Proactively tells the reviewer, BEFORE they've reviewed anything, that a section's
// data is byte-identical across some/all of a company's models — a complement to
// useSharedSectionReview, which only surfaces an inherited status/notes AFTER someone
// has actually reviewed a matching section on another model. Both read the same
// dataset.sectionContentHash; this one doesn't require a decided status to exist yet.
//
// Relies on usePrefetchOtherModels having kicked off each sibling's load. useQueries
// uses the identical queryKey shape ['mmmDataQALoad', cid, configPath], so it observes
// those same cache entries reactively (re-rendering as each prefetch settles) rather
// than triggering a duplicate fetch — React Query dedupes concurrent observers of the
// same key. Gated on `activeModelLoaded`, mirroring usePrefetchOtherModels's own
// `activeLoadResult` gate: without it, this hook's useQueries fires N concurrent
// loadConfig fetches for every sibling model as soon as `configs`/`activeConfigPath`
// are set (right after the initial config fetch, well before the active model itself
// has loaded), reintroducing the exact "competed with the active model for Lambda/DB
// concurrency" regression usePrefetchOtherModels's sequential design exists to avoid.
export const useSectionDataInvariance = (
  cid: string | undefined,
  configs: ConfigSummary[],
  activeConfigPath: string | undefined,
  activeSectionContentHash: Record<string, string> | undefined,
  // Which dataset.sectionContentHash keys to compare -- callers pass SUMMARY_SECTIONS
  // for the approve/flag sections, plus SectionKey.CAMPAIGNS where relevant (Campaigns
  // has no approve/flag status, but still benefits from the same "don't make the
  // reviewer check every model" signal).
  sectionKeys: SectionKeyType[],
  activeModelLoaded: boolean
): Partial<Record<SectionKeyType, SectionDataInvariance>> => {
  // Grouped by MODEL, not config: Brand Equity's two stage configs must be compared
  // (and named) as a single model, and the active model's OWN other stage must be
  // excluded entirely as "another model" rather than compared against itself.
  const otherGroups = useMemo(() => {
    if (!cid || !activeConfigPath) return [];
    return groupConfigsByModel(configs).filter(
      group => !group.configs.some(config => config.configPath === activeConfigPath)
    );
  }, [cid, activeConfigPath, configs]);

  // Memoized deliberately: useQueries treats a new `queries` array as a new set of
  // observers on every call, even when the queryKeys inside are unchanged — passing a
  // freshly `.map()`-built array on every render causes observer churn that can pin
  // the render loop, especially while sibling queries are still pending.
  const otherConfigs = useMemo(() => otherGroups.flatMap(group => group.configs), [otherGroups]);

  const queries = useMemo(
    () =>
      otherConfigs.map(config => ({
        queryKey: ['mmmDataQALoad', cid, config.configPath],
        queryFn: () => loadConfig(cid ?? '', config.configPath),
        staleTime: Infinity,
        enabled: activeModelLoaded
      })),
    [otherConfigs, cid, activeModelLoaded]
  );

  const results = useQueries({ queries });

  return useMemo(() => {
    if (!activeSectionContentHash) return {};

    const resultByConfigPath = new Map<string, (typeof results)[number]>();
    otherConfigs.forEach((config, i) => resultByConfigPath.set(config.configPath, results[i]));

    const invariance: Partial<Record<SectionKeyType, SectionDataInvariance>> = {};
    for (const sectionKey of sectionKeys) {
      const activeHash = activeSectionContentHash[sectionKey];
      let checking = false;
      const matchingModelLabels: string[] = [];

      for (const group of otherGroups) {
        const matchedConfigs: ConfigSummary[] = [];
        let groupPending = false;
        for (const config of group.configs) {
          const result = resultByConfigPath.get(config.configPath);
          if (!result || result.status === 'pending') {
            groupPending = true;
            continue;
          }
          if (result.status !== 'success') continue; // errored — resolved, no match
          const otherHash = result.data.dataset.sectionContentHash[sectionKey];
          if (activeHash && otherHash === activeHash) {
            matchedConfigs.push(config);
          }
        }
        // Independent, not else-if: a group can have SOME configs already matched
        // while another of its own configs is still pending (e.g. Brand Equity's s1
        // resolved and matched, s2 hasn't loaded yet) -- that group's own label is
        // still provisional (s2 settling could turn "Brand Equity (GQV)" into the
        // full "Brand Equity", or leave it as-is), so `checking` must stay true for
        // it even though a partial label was already pushed. Whether the UI shows
        // the spinner or the provisional label (or both) while checking is true is
        // the caller's call, not this hook's.
        if (matchedConfigs.length > 0) {
          matchingModelLabels.push(matchLabel(group, matchedConfigs));
        }
        if (groupPending) {
          checking = true;
        }
      }

      invariance[sectionKey] = { checking, matchingModelLabels };
    }
    return invariance;
  }, [activeSectionContentHash, otherGroups, otherConfigs, results, sectionKeys]);
};
